import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Outlet,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import i18next from "i18next";
import { localizationLanguageAction } from "features/language/redux/actions/lang_action";
import CenterLoading from "components/common/CenterLoading";
import { ToastComponent } from "../library/components/toast.component";
import openApi from "services/apis/openApi";
import proxyApi from "services/apis/proxyApi";
import pinApi from "services/apis/pinApi";
import { getPathPrefix, isInitialPath } from "utils/coreUtils";
import {
  setCompanyDetails,
  resetCompanyDetails,
} from "store/reducers/company.slice";

export default function ValidateRouteDetails() {
  const [isLoading, setIsLoading] = useState(true);
  const [pageError, setPageError] = useState();

  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const dispatch = useDispatch();
  const companyDetails = useSelector((state) => state?.company);

  const [searchParams] = useSearchParams();

  const getValidPath = (currPath) => {
    const languageCode = localStorage.getItem("languageCode") ?? "en";
    const domainName = localStorage.getItem("companyDomain");

    if (params?.languageCode !== languageCode) {
      return currPath.replace(
        params?.companyDomain + "/" + params?.languageCode,
        domainName + "/" + languageCode
      );
    }

    return currPath;
  };

  const checkForNewUser = async (companyDomain) => {
    const currPath = location.pathname + location.search;

    /**
     * lt: Login type
     * cid: Company id
     * cp: Company pin
     *
     * If above params are exist in url then logout other and login again.
     */
    if (
      searchParams.has("lt") &&
      searchParams.has("cid") &&
      searchParams.has("cp")
    ) {
      if (searchParams.get("lt") === "admin") {
        try {
          const { data } = await pinApi.verifyPinAndGenerateAccessToken({
            companyId: searchParams.get("cid"),
            pin: searchParams.get("cp"),
          });

          localStorage.setItem("authToken", data?.jwttoken);
          localStorage.setItem("companyUserId", data?.companyUserId);
          localStorage.setItem("companyId", data?.companyId);
          localStorage.removeItem("encKeyLink");

          await proxyApi.generateAccessToken({
            companyId: data?.companyId,
          });

          navigate(getPathPrefix("dashboard"), {
            replace: true,
          });
          setIsLoading(false);
          return;
        } catch (error) {
          handleError(error);
          setIsLoading(false);
          return;
        }
      }
    }

    setIsLoading(false);

    if (!localStorage.getItem("authToken")) {
      if (
        window.location.href.indexOf("encyptedKey") > 0 ||
        window.location.href.indexOf("new-user") > 0
      ) {
        navigate(getPathPrefix("new-user"), {
          state: { encUrl: window.location.href },
          replace: true,
        });
      } else if (
        isInitialPath(location.pathname) ||
        currPath === "/" + companyDomain ||
        currPath === "/" + companyDomain + "/"
      ) {
        navigate(getPathPrefix("login"), {
          replace: true,
        });
      } else {
        navigate(getValidPath(currPath), {
          replace: true,
        });
      }
    } else {
      // TODO: comment last 2 condition in when dynamic domain integrated
      if (
        isInitialPath(location.pathname) ||
        currPath === "/" + companyDomain ||
        currPath === "/" + companyDomain + "/"
      ) {
        navigate(getPathPrefix("dashboard"), {
          replace: true,
        });
      } else {
        navigate(getValidPath(currPath), {
          replace: true,
        });
      }
    }
  };

  const handleError = (error) => {
    if (
      error.status === 400 ||
      error?.response?.status === 400 ||
      error?.response?.data?.status === -1
    ) {
      if (typeof error?.response?.data === "string") {
        setPageError(error?.response?.data);
      } else if (typeof error?.response?.data?.message === "string") {
        setPageError(error?.response?.data?.message);
      } else if (typeof error?.response?.data?.response === "string") {
        setPageError(error?.response?.data?.response);
      } else {
        setPageError("Facing issue from the server");
      }
    } else {
      // even if facing error, redirect user to the specific page.
      // getLanguageAndRedirect();
    }
  };

  const isDifferentDomain = (companyDomain) => {
    if (companyDomain !== params?.companyDomain) {
      return true;
    }

    return false;
  };

  const getCompanyAccessDetail = async () => {
    const companyDomain = params?.companyDomain;

    if (companyDomain) {
      try {
        await openApi.getCompanyInformationByDomainName({
          domainName: companyDomain,
        });
        const response = await openApi.getCompanyAccessDetail({
          accessUrl: companyDomain,
        });
        const companyData = {
          companyId: response?.data?.companyId,
          domainName: companyDomain,
          languageCode: response?.data?.companyLanguageCode,
          companyName: response?.data?.companyName,
          companySubscriptionValidity:
            response?.data?.companySubscriptionValidity,
          defaultTimeZone: response?.data?.defaultTimeZone,
        };
        dispatch(setCompanyDetails(companyData));

        return companyData;
      } catch (error) {
        handleError(error);
      }
    }
  };

  const getCompanyDetails = async () => {
    setIsLoading(true);
    let companyData = companyDetails;
    // if domain is different then refresh all and remove logged in data.
    if (isDifferentDomain(companyData?.domainName)) {
      localStorage.clear();
      dispatch(resetCompanyDetails());
      companyData = await getCompanyAccessDetail();
      localStorage.setItem("languageCode", companyData?.languageCode);
      localStorage.setItem("companyDomain", companyData?.domainName);
    }

    const langCode = companyData?.languageCode ?? "en";

    if (langCode) {
      dispatch(localizationLanguageAction(langCode))
        .then((langData) => {
          i18next.addResourceBundle(langCode, "translation", langData);
          i18next.changeLanguage(langCode);
          checkForNewUser(companyData?.domainName);
        })
        .catch(() => {
          checkForNewUser(companyData?.domainName);
        });
      return;
    }

    // Check if domain is passed or not in URL or not for example https://host/
    if (!params?.companyDomain) {
      // if not passed then check domain in localStorage
      const companyDomain = companyData?.domainName;

      // if there also domain is not available then show error
      if (!companyDomain) {
        setIsLoading(false);
        setPageError(
          "You're on invalid page. Please use URL provided to you for login purpose."
        );
      }
      return;
    }
  };

  useEffect(() => {
    getCompanyDetails();
  }, []);
  if (isLoading) return <CenterLoading minHeight="100vh" />;
  // Need to show full screen loading while getting token from the admin
  return (
    <>
      {pageError ? (
        <div
          className="row text-center align-items-center m-0 text-danger fw-bold"
          style={{
            height: "100vh",
          }}
        >
          <div>
            <h1 style={{ fontWeight: "bolder" }}>ERROR</h1>
            {pageError && <p className="mb-0">{pageError}</p>}
            <p>Contact Scandi Automation admin for support.</p>
          </div>
        </div>
      ) : (
        <Outlet />
      )}

      <ToastComponent />
    </>
  );
}
